import { Tree, type TreeDataNode } from 'antd'
import { Code2, Pencil } from 'lucide-react'
import type { SystemPermission } from '@/api/types/system'
import { PERMISSION_RESOURCE_TYPE_LABELS } from '@/api/types/system'

type PermissionTreeProps = {
  permissions: SystemPermission[]
  checkedIds?: number[]
  onCheckedChange?: (permissionIds: number[]) => void
  onEdit?: (permission: SystemPermission) => void
}

const MODULE_LABELS: Record<string, string> = {
  dashboard: '工作台',
  work: '工单管理',
  sales: '销售',
  procurement: '采购管理',
  inventory: '物料库存',
  asset: '设备管理',
  maintenance: '维保计划',
  customer: '客户管理',
  support: '通用支持',
  system: '系统管理',
}

export function PermissionTree({ permissions, checkedIds, onCheckedChange, onEdit }: PermissionTreeProps) {
  const groups = new Map<string, SystemPermission[]>()
  permissions.forEach((permission) => {
    const module = permission.permissionCode.split(':')[0] || 'other'
    groups.set(module, [...(groups.get(module) ?? []), permission])
  })
  const treeData: TreeDataNode[] = [...groups.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([module, items]) => ({
    key: `module:${module}`,
    title: <span className="font-semibold text-gray-800">{MODULE_LABELS[module] ?? module}</span>,
    children: items.map((permission) => ({
      key: String(permission.id),
      disabled: !permission.enabled,
      title: <PermissionNode permission={permission} onEdit={onEdit} />,
    })),
  }))

  return <Tree
    blockNode
    checkable={Boolean(onCheckedChange)}
    defaultExpandAll
    checkedKeys={checkedIds?.map(String)}
    onCheck={(keys) => onCheckedChange?.((keys as Array<string | number>)
      .map(String).filter((key) => /^\d+$/.test(key)).map(Number))}
    treeData={treeData}
    className="permission-tree rounded-xl border border-gray-100 bg-slate-50/50 p-3"
  />
}

function PermissionNode({ permission, onEdit }: { permission: SystemPermission; onEdit?: (permission: SystemPermission) => void }) {
  const typeTone = permission.resourceType === 'MENU' ? 'bg-blue-50 text-blue-700' : permission.resourceType === 'BUTTON' ? 'bg-violet-50 text-violet-700' : 'bg-amber-50 text-amber-700'
  return <div className="flex min-w-0 items-center gap-2 py-1.5 pr-2">
    <Code2 className="h-3.5 w-3.5 shrink-0 text-gray-400" />
    <span className="min-w-0 flex-1"><span className="font-medium text-gray-800">{permission.permissionName}</span><span className="ml-2 font-mono text-xs text-gray-400">{permission.permissionCode}</span>{permission.resourcePath && <span className="ml-2 hidden text-xs text-gray-400 lg:inline">{permission.resourcePath}</span>}</span>
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${typeTone}`}>{PERMISSION_RESOURCE_TYPE_LABELS[permission.resourceType]}</span>
    {!permission.enabled && <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">停用</span>}
    {onEdit && <button type="button" onClick={(event) => { event.stopPropagation(); onEdit(permission) }} className="shrink-0 rounded p-1 text-primary-600 hover:bg-primary-50" title="编辑权限"><Pencil className="h-3.5 w-3.5" /></button>}
  </div>
}
